import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, Clock, MapPin, ExternalLink } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { publicApi } from '@/lib/api';

interface FooterLink {
  labelKey: string;
  href: string;
  external?: boolean;
}

interface PopularRoute {
  originCity: { name: string; state: string };
  destinationCity: { name: string; state: string };
  lowestPrice: string;
}

const quickLinks: FooterLink[] = [
  { labelKey: 'landing.trackMyTrip', href: '#' },
  { labelKey: 'landing.termsConditions', href: '#' },
  { labelKey: 'landing.cancellationPolicy', href: '#' },
  { labelKey: 'landing.privacyPolicy', href: '#' },
];

/**
 * EnhancedFooter - Professional multi-column footer for the Pegasos landing page
 *
 * Features:
 * - Deep blue background with subtle texture
 * - Four-column layout on desktop, stacked on mobile
 * - Contact info, quick links, routes, and company info
 * - Elegant hover effects on links
 * - Bottom bar with powered by and copyright
 */
export function EnhancedFooter() {
  const { t } = useTranslation();
  const { tenant, slug } = useTenant();
  const currentYear = new Date().getFullYear();
  const [popularRoutes, setPopularRoutes] = useState<PopularRoute[]>([]);

  // Get brand color from tenant context with fallback
  const brandBlue = tenant?.branding?.primaryColor || '#00409B';

  // Fetch popular routes from API
  useEffect(() => {
    if (!slug) return;

    let cancelled = false;

    async function fetchRoutes() {
      try {
        const response = await publicApi.get<{ routes: PopularRoute[] }>(
          `/api/public/${slug}/popular-routes`
        );
        if (!cancelled) {
          setPopularRoutes((response.routes || []).slice(0, 4)); // Limit to 4 routes for footer
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch popular routes:', error);
        }
      }
    }

    fetchRoutes();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <footer
      id="contact"
      className="relative overflow-hidden scroll-mt-20"
      style={{ backgroundColor: brandBlue }}
    >
      {/* Subtle radial gradient overlay for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.05) 0%, transparent 50%)`,
        }}
        aria-hidden="true"
      />

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12 md:py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Contact Column */}
          <div>
            <h3
              className="text-lg font-semibold mb-5 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t('landing.contact')}
            </h3>
            <ul className="space-y-4">
              {tenant?.contact?.phoneUs && (
                <li>
                  <a
                    href={`tel:${tenant.contact.phoneUs.replace(/[^+\d]/g, '')}`}
                    className="flex items-start gap-3 text-white/80 hover:text-white transition-colors group"
                  >
                    <Phone
                      className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform"
                      aria-hidden="true"
                    />
                    <span>
                      <span className="block text-xs text-white/50 uppercase tracking-wide mb-0.5">
                        USA
                      </span>
                      {tenant.contact.phoneUs}
                    </span>
                  </a>
                </li>
              )}
              {tenant?.contact?.phoneMx && (
                <li>
                  <a
                    href={`tel:${tenant.contact.phoneMx.replace(/[^+\d]/g, '')}`}
                    className="flex items-start gap-3 text-white/80 hover:text-white transition-colors group"
                  >
                    <Phone
                      className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform"
                      aria-hidden="true"
                    />
                    <span>
                      <span className="block text-xs text-white/50 uppercase tracking-wide mb-0.5">
                        Mexico
                      </span>
                      {tenant.contact.phoneMx}
                    </span>
                  </a>
                </li>
              )}
              {tenant?.contact?.email && (
                <li>
                  <a
                    href={`mailto:${tenant.contact.email}`}
                    className="flex items-start gap-3 text-white/80 hover:text-white transition-colors group"
                  >
                    <Mail
                      className="w-4 h-4 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform"
                      aria-hidden="true"
                    />
                    <span className="break-all">{tenant.contact.email}</span>
                  </a>
                </li>
              )}
              {tenant?.contact?.operatingHours && (
                <li className="flex items-start gap-3 text-white/70">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>{tenant.contact.operatingHours}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3
              className="text-lg font-semibold mb-5 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t('landing.quickLinks')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.labelKey}>
                  <a
                    href={link.href}
                    className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors relative group"
                  >
                    <span className="relative">
                      {t(link.labelKey)}
                      <span
                        className="absolute bottom-0 left-0 w-0 h-px bg-white/50 group-hover:w-full transition-all duration-300"
                        aria-hidden="true"
                      />
                    </span>
                    {link.external && (
                      <ExternalLink className="w-3 h-3 opacity-50" aria-hidden="true" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Routes Column */}
          <div>
            <h3
              className="text-lg font-semibold mb-5 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t('landing.routes')}
            </h3>
            <ul className="space-y-3">
              {popularRoutes.map((route) => (
                <li key={`${route.originCity.name}-${route.destinationCity.name}`}>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex items-start gap-2 text-white/80 hover:text-white transition-colors text-left group"
                  >
                    <MapPin
                      className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                    <span>
                      {route.originCity.name}
                      <span className="mx-1.5 text-white/40">→</span>
                      {route.destinationCity.name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3
              className="text-lg font-semibold mb-5 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {tenant?.name || 'Bus Company'}
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {tenant?.companyDescription ||
                'Connecting families and communities between Texas and Mexico for over a decade. Travel with comfort, safety, and trust.'}
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-4">
              {tenant?.social?.facebook && (
                <a
                  href={tenant.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all hover:scale-110"
                  aria-label="Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                  </svg>
                </a>
              )}
              {tenant?.social?.instagram && (
                <a
                  href={tenant.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all hover:scale-110"
                  aria-label="Instagram"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div
        className="border-t relative"
        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-white/50">
            <span>{t('common.poweredBy')}</span>
            <span>
              © {currentYear} {tenant?.name || 'Bus Company'} S.A. de C.V.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default EnhancedFooter;
